import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Coins, Trophy, Clock, CheckCircle2, ShieldCheck, Flag, Users, Share2, Ban, CreditCard, Smartphone, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import RaceExperience from "@/components/RaceExperience";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const LOGO = "https://customer-assets-jai6qajn.emergentagent.net/job_type-race-4/artifacts/a9pwlfiw_white_text_racing.png";
const flyerSrc = (c) => (c?.flyer_image_url ? `${BACKEND}${c.flyer_image_url}` : LOGO);

const PAY_METHODS = [
  { id: "stripe", label: "Card / Apple Pay", desc: "Visa, Mastercard, Apple Pay & more", Icon: CreditCard, enabled: true },
  { id: "cashapp", label: "Cash App Pay", desc: "Approve in Cash App — scan a QR or enter your code", Icon: Smartphone, enabled: true },
  { id: "paypal", label: "PayPal", desc: "Coming soon", Icon: Coins, enabled: false },
  { id: "venmo", label: "Venmo", desc: "Coming soon", Icon: Coins, enabled: false },
];

export default function ContestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [c, setC] = useState(null);
  const [board, setBoard] = useState([]);
  const [race, setRace] = useState(null);
  const [busy, setBusy] = useState(false);
  const [geoStatus, setGeoStatus] = useState(null);
  const [showPay, setShowPay] = useState(false);
  const [payMethod, setPayMethod] = useState(null);
  const [paying, setPaying] = useState(false);

  const load = () => {
    api.get(`/contests/${id}`).then((r) => setC(r.data)).catch(() => toast.error("Contest not found"));
    api.get(`/contests/${id}/leaderboard`).then((r) => setBoard(r.data)).catch(() => {});
  };
  useEffect(load, [id]);

  useEffect(() => {
    if (c && (c.entry_fee_credits > 0 || c.prize_pool_credits > 0)) {
      api.get("/geo/status").then((r) => setGeoStatus(r.data)).catch(() => {});
    }
  }, [c]);

  const register = async () => {
    setBusy(true);
    try {
      await api.post(`/contests/${id}/register`);
      toast.success("Registered - you're on the grid");
      refreshUser();
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const startRace = () => navigate(`/lobby/${id}`);

  const entryUsd = c ? (Number(c.entry_fee_credits || 0) / 100).toFixed(2) : "0.00";

  const startEntryCheckout = async () => {
    if (!payMethod) return;
    setPaying(true);
    try {
      const { data } = await api.post(`/contests/${id}/entry/checkout`,
        { method: payMethod, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      setPaying(false);
    }
  };

  // Confirm a cash entry when Stripe sends the racer back (?paid=1&session_id=...).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    if (!sid || !params.get("paid")) return;
    let stop = false, tries = 0;
    const clearUrl = () => window.history.replaceState({}, "", `/contests/${id}`);
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          toast.success("Payment received — you're on the grid!");
          refreshUser(); load(); clearUrl(); return;
        }
      } catch { /* retry */ }
      tries += 1;
      if (!stop && tries < 12) setTimeout(poll, 2500);
      else if (!stop) {
        toast.message("Payment is processing", { description: "We'll confirm your entry shortly." });
        clearUrl();
      }
    };
    poll();
    return () => { stop = true; };
    // eslint-disable-next-line
  }, []);

  if (!c) return <div className="text-race-sub">Loading…</div>;

  const start = new Date(c.start_time);
  const StatusIcon = c.status === "confirmed" ? CheckCircle2 : Clock;
  const geoBlocked = (c.entry_fee_credits > 0 || c.prize_pool_credits > 0) && geoStatus?.restricted;

  return (
    <div className="max-w-3xl mx-auto animate-slide-up">
      {c.status === "cancelled" && (
        <div className="mb-6 border border-race-red bg-race-red/10 rounded-sm p-4" data-testid="contest-cancelled-banner">
          <div className="flex items-center gap-2 text-race-red font-heading text-xl tracking-tight">
            <Ban size={18} /> CONTEST CANCELLED
          </div>
          <p className="text-race-txt text-sm mt-1">This contest has been cancelled by IBRA. Any entry fee has been refunded to your account.</p>
          {c.cancel_reason && (
            <p className="text-race-sub text-sm mt-2 border-l-2 border-race-red pl-3">{c.cancel_reason}</p>
          )}
        </div>
      )}
      {/* Sponsor-themed header (Section 8) */}
      <div className="relative border border-race-line noise overflow-hidden mb-6">
        <div className="w-full bg-race-elevated flex items-center justify-center overflow-hidden" style={{ aspectRatio: "16 / 6" }}>
          <img src={flyerSrc(c)} alt={`${c.name} flyer`} data-testid="contest-flyer-img"
            className={c.flyer_image_url ? "w-full h-full object-cover" : "h-16 w-auto opacity-90"} />
        </div>
        {c.sponsor_name && <div className="absolute inset-0 bg-gradient-to-r from-race-info/20 to-transparent" />}
        <div className="relative p-6">
          <div className="flex items-center gap-2 mb-1">
            <span className={`flex items-center gap-1 text-[10px] font-mono uppercase tracking-wide ${c.status === "confirmed" ? "text-race-volt" : "text-race-sub"}`}>
              <StatusIcon size={12} /> {c.status}
            </span>
            <span className="text-[10px] font-mono text-race-muted uppercase">· {c.race_type}</span>
          </div>
          <h1 className="font-mono text-3xl md:text-4xl text-race-txt break-words">{c.name}</h1>
          {c.sponsor_name && (
            <div className="overline text-race-info mt-2">Presented by {c.sponsor_name}{c.sponsor_tagline ? ` - ${c.sponsor_tagline}` : ""}</div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Meta Icon={Clock} label="Starts" value={`${start.toLocaleDateString()} ${start.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`} />
        <Meta Icon={Trophy} label="Podium" value={c.podium_size === 1 ? "Winner takes all" : `Top ${c.podium_size}`} />
        <Meta Icon={Coins} label="Entry" value={c.entry_fee_credits > 0 ? `${c.entry_fee_credits} cr` : "Free"} />
        <Meta Icon={Coins} label="Prize Pool" value={`${c.prize_pool_credits} cr`} accent />
        <Meta Icon={Users} label="Registrants" value={c.registrant_count} />
        <Meta Icon={Flag} label="Track" value={c.track_type} />
        <Meta Icon={Flag} label="Buttons" value={c.transmission} />
        <Meta Icon={Clock} label="Penalty" value={`${c.penalty_seconds}s`} />
      </div>

      {c.br_protocol_url && (
        <div className="bg-race-volt/10 border border-race-volt/40 rounded-sm p-4 mb-6 flex items-center gap-3" data-testid="ibra-certified">
          <div className="h-9 w-9 shrink-0 rounded-full bg-race-volt/20 flex items-center justify-center">
            <ShieldCheck size={20} className="text-race-volt" />
          </div>
          <div className="min-w-0">
            <div className="font-heading text-lg tracking-wide text-race-volt leading-none">IBRA CERTIFIED</div>
            <div className="text-race-sub text-xs mt-1">Domain Estate verified on the 4th AVE repository</div>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-stretch gap-3 mb-8">
        {c.status === "completed" ? (
          <button onClick={() => navigate(`/results/${id}`)} data-testid="view-results-btn"
            className="w-full sm:w-auto text-center bg-race-volt text-race-bg font-heading text-xl sm:text-2xl tracking-wide px-8 py-3.5 rounded-sm hover:opacity-90 transition-opacity">
            VIEW FINAL RESULTS
          </button>
        ) : !c.is_registered ? (
          c.entry_fee_credits > 0 ? (
            <button onClick={() => { setShowPay(true); setPayMethod(null); }} disabled={busy || geoBlocked || !user?.has_license} data-testid="pay-entry-btn"
              className="w-full sm:w-auto text-center bg-race-red hover:bg-race-redhover disabled:opacity-50 text-race-bg font-heading text-xl sm:text-2xl tracking-wide px-8 py-3.5 rounded-sm transition-colors">
              PAY TO ENTER - ${entryUsd}
            </button>
          ) : (
            <button onClick={register} disabled={busy || geoBlocked} data-testid="register-contest-btn"
              className="w-full sm:w-auto text-center bg-race-red hover:bg-race-redhover disabled:opacity-50 text-race-bg font-heading text-xl sm:text-2xl tracking-wide px-8 py-3.5 rounded-sm transition-colors">
              REGISTER - FREE
            </button>
          )
        ) : (
          <button onClick={startRace} disabled={busy} data-testid="start-contest-btn"
            className="w-full sm:w-auto text-center bg-race-volt text-race-bg font-heading text-xl sm:text-2xl tracking-wide px-8 py-3.5 rounded-sm hover:opacity-90 transition-opacity">
            ENTER THE START-LINE
          </button>
        )}
        <div className="grid grid-cols-3 gap-2 sm:flex sm:gap-3">
          <button onClick={() => navigate("/dashboard")} className="flex items-center justify-center border border-race-line hover:border-race-txt text-race-txt text-sm px-4 py-3 rounded-sm transition-colors">Back</button>
          <Link to={`/flyer/${c.id}`} data-testid="contest-flyer-link" className="flex items-center justify-center gap-1.5 border border-race-line hover:border-race-txt text-race-txt text-sm px-4 py-3 rounded-sm transition-colors">
            <Share2 size={15} /> Flyer
          </Link>
        </div>
      </div>

      {showPay && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-0 sm:p-4" data-testid="pay-portal" onClick={() => !paying && setShowPay(false)}>
          <div className="w-full sm:max-w-md bg-race-surface border border-race-line rounded-t-2xl sm:rounded-lg p-5 animate-slide-up" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-1">
              <div>
                <h3 className="font-heading text-2xl tracking-tight text-race-txt">PAY TO ENTER</h3>
                <p className="text-race-sub text-sm mt-0.5">{c.name}</p>
              </div>
              <button onClick={() => !paying && setShowPay(false)} data-testid="pay-close" className="text-race-sub hover:text-race-txt p-1"><X size={20} /></button>
            </div>
            <div className="font-heading text-3xl text-race-volt mb-4" data-testid="pay-amount">${entryUsd}</div>
            <p className="overline mb-2">Choose a payment method</p>
            <div className="space-y-2">
              {PAY_METHODS.map((m) => {
                const selected = payMethod === m.id;
                return (
                  <button key={m.id} type="button" disabled={!m.enabled || paying}
                    data-testid={`pay-method-${m.id}`}
                    aria-pressed={selected}
                    onClick={() => m.enabled && setPayMethod(selected ? null : m.id)}
                    className={`w-full flex items-center gap-3 text-left px-4 py-3 rounded-md border transition-colors ${
                      !m.enabled ? "opacity-40 cursor-not-allowed border-race-line"
                      : selected ? "border-race-volt bg-race-volt/10" : "border-race-line hover:border-race-txt"}`}>
                    <span className={`h-5 w-5 rounded-full border flex items-center justify-center shrink-0 ${selected ? "border-race-volt" : "border-race-sub"}`}>
                      {selected && <span className="h-2.5 w-2.5 rounded-full bg-race-volt" />}
                    </span>
                    <m.Icon size={20} className={m.enabled ? "text-race-txt" : "text-race-sub"} />
                    <span className="flex-1">
                      <span className="block text-race-txt text-sm font-medium">{m.label}{!m.enabled && <span className="ml-2 text-[10px] uppercase tracking-widest text-race-sub border border-race-line rounded px-1.5 py-0.5">Soon</span>}</span>
                      <span className="block text-race-sub text-xs">{m.desc}</span>
                    </span>
                  </button>
                );
              })}
            </div>
            <button onClick={startEntryCheckout} disabled={!payMethod || paying} data-testid="pay-submit"
              className="mt-5 w-full bg-race-red hover:bg-race-redhover disabled:opacity-40 text-race-bg font-heading text-xl tracking-wide px-6 py-3 rounded-sm transition-colors">
              {paying ? "REDIRECTING…" : payMethod ? `PAY $${entryUsd}` : "SELECT A METHOD"}
            </button>
            <p className="text-race-muted text-[11px] mt-3 text-center">Secure checkout powered by Stripe. You won't be charged until you confirm.</p>
          </div>
        </div>
      )}

      {!user?.has_license && (
        <p className="text-race-red text-sm mb-6">A BR License in good standing is required to enter. Claim yours from the dashboard.</p>
      )}

      {geoBlocked && (
        <p className="text-race-red text-sm mb-6" data-testid="geo-blocked-notice">
          Real-money contests aren't available in your region{geoStatus?.region ? ` (${geoStatus.region})` : ""}. Free practice is always open.
        </p>
      )}

      <h2 className="font-heading text-3xl tracking-tight mb-3">LEADERBOARD</h2>
      {c.post_race_ad_url && (
        <img src={`${BACKEND}${c.post_race_ad_url}`} alt="Sponsor" data-testid="contest-postrace-ad"
          className="w-full rounded-sm border border-race-line mb-4 object-cover" />
      )}
      <div className="bg-race-surface border border-race-line">
        {board.length === 0 && <p className="p-4 text-race-sub text-sm">No validated finishes yet.</p>}
        {board.map((r, i) => (
          <div key={i} className="flex items-center justify-between px-4 py-2.5 border-b border-race-line/60 last:border-0 font-mono text-sm" data-testid={`board-row-${i}`}>
            <span className="text-race-txt">{i + 1}. {r.name}</span>
            <span className="flex gap-4 tabular text-race-sub"><span className="text-race-volt">{r.cps} cps</span> <span>{r.final_time}s</span></span>
          </div>
        ))}
      </div>
    </div>
  );
}

const Meta = ({ Icon, label, value, accent }) => (
  <div className="bg-race-surface border border-race-line p-3">
    <div className="flex items-center gap-1 overline"><Icon size={11} /> {label}</div>
    <div className={`font-mono text-sm mt-1 capitalize ${accent ? "text-race-volt" : "text-race-txt"}`}>{value}</div>
  </div>
);
